import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Library, Youtube, FileText, Link as LinkIcon, Trash2, Search, Users2, ClipboardList, ExternalLink, BookOpen, Plus, X, Upload } from "lucide-react";
import { api } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** All resources the teacher has uploaded — across every group, assignment,
 * and chapter — in one scrollable list. Top-level alternative to drilling
 * into group or chapter panels. Supports quick delete, filter by kind, and
 * a built-in "Attach to chapter" flow. */
export default function TeacherResourcesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [attachOpen, setAttachOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff/resources");
      setItems(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((r) => {
      if (kind !== "all" && r.type !== kind) return false;
      if (!t) return true;
      return (r.title || "").toLowerCase().includes(t)
        || (r.group_name || "").toLowerCase().includes(t)
        || (r.assignment_title || "").toLowerCase().includes(t)
        || (r.chapter_title || "").toLowerCase().includes(t);
    });
  }, [items, q, kind]);

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? Students won't see it anymore.`)) return;
    await api.delete(`/staff/resources/${r.resource_id}`);
    load();
  };

  const counts = useMemo(() => ({
    all: items.length,
    youtube: items.filter((r) => r.type === "youtube").length,
    file: items.filter((r) => r.type === "file").length,
    link: items.filter((r) => r.type === "link").length,
  }), [items]);

  return (
    <div className="space-y-4" data-testid="teacher-resources-tab">
      {/* Search + kind filter + attach CTA */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search resources by title, chapter, group, or assignment…"
            data-testid="resources-search"
            className="w-full ps-10 pe-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
          />
        </div>
        <div className="inline-flex items-center gap-1 bg-white border border-border rounded-xl p-1 overflow-x-auto">
          <FilterPill active={kind === "all"} onClick={() => setKind("all")} label="All" count={counts.all} />
          <FilterPill active={kind === "youtube"} onClick={() => setKind("youtube")} label="YouTube" count={counts.youtube} />
          <FilterPill active={kind === "file"} onClick={() => setKind("file")} label="PDFs" count={counts.file} />
          <FilterPill active={kind === "link"} onClick={() => setKind("link")} label="Links" count={counts.link} />
        </div>
        <button
          onClick={() => setAttachOpen(true)}
          data-testid="attach-to-chapter-btn"
          className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-white font-medium px-4 py-2.5 rounded-xl transition-all whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> Attach to chapter
        </button>
      </div>

      {loading && (
        <div className="bg-white border border-border rounded-2xl p-8 text-center text-muted-foreground">
          Loading resources…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-border rounded-2xl p-10 text-center" data-testid="resources-empty">
          <div className="mx-auto w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center mb-3">
            <Library className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h3 className="font-display text-lg font-semibold mb-1">
            {q || kind !== "all" ? "No match" : "No resources yet"}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Click <span className="font-medium text-foreground">Attach to chapter</span> to share a presentation,
            video, or link with every student on a specific chapter — or open any group in the
            <span className="font-medium text-foreground"> Groups</span> tab for private group resources.
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="resources-grid">
          {filtered.map((r) => (
            <ResourceCard key={r.resource_id} resource={r} onDelete={() => remove(r)} />
          ))}
        </div>
      )}

      {attachOpen && (
        <AttachToChapterModal
          onClose={() => setAttachOpen(false)}
          onSaved={() => { setAttachOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function FilterPill({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
        active ? "bg-sage text-white" : "text-muted-foreground hover:bg-secondary"
      }`}
    >
      {label} <span className={active ? "opacity-80" : "opacity-60"}>({count})</span>
    </button>
  );
}

const RESOURCE_META = {
  youtube: { Icon: Youtube, iconColor: "text-[#d8382b]", hrefKey: "youtube_url" },
  file: { Icon: FileText, iconColor: "text-sage", hrefKey: null /* built dynamically */ },
  link: { Icon: LinkIcon, iconColor: "text-muted-foreground", hrefKey: "external_url" },
};

function ResourceCard({ resource: r, onDelete }) {
  const meta = RESOURCE_META[r.type] || RESOURCE_META.link;
  const Icon = meta.Icon;
  const fileUrl = r.type === "file" && r.file_id ? `${BACKEND}/api/files/${r.file_id}` : null;
  const href = r.type === "file" ? fileUrl : r[meta.hrefKey];

  return (
    <div className="bg-white border border-border rounded-2xl p-4 flex flex-col gap-3" data-testid={`resource-${r.resource_id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0 ${meta.iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium leading-tight line-clamp-2">{r.title || "(untitled)"}</div>
          {r.description && (
            <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
          )}
        </div>
      </div>

      {r.type === "youtube" && r.youtube_id && (
        <div className="aspect-video rounded-lg overflow-hidden bg-bone border border-border">
          <iframe
            src={`https://www.youtube.com/embed/${r.youtube_id}`}
            title={r.title}
            className="w-full h-full"
            allowFullScreen
          />
        </div>
      )}

      <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
        {r.chapter_title && (
          <span className="inline-flex items-center gap-1 bg-sage/10 text-sage px-1.5 py-0.5 rounded" data-testid={`resource-chapter-${r.resource_id}`}>
            <BookOpen className="w-3 h-3" /> Ch{String(r.chapter_number ?? "").padStart(2, "0")} · {r.chapter_title}
          </span>
        )}
        {r.group_name && (
          <span className="inline-flex items-center gap-1">
            <Users2 className="w-3 h-3" /> {r.group_name}
          </span>
        )}
        {r.assignment_title && (
          <span className="inline-flex items-center gap-1">
            <ClipboardList className="w-3 h-3" /> {r.assignment_title}
          </span>
        )}
        <span>{new Date(r.created_at).toLocaleDateString()}</span>
      </div>

      <div className="flex items-center gap-2 mt-auto">
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium"
            data-testid={`resource-open-${r.resource_id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open
          </a>
        )}
        <button
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-terracotta/10 hover:text-terracotta text-xs font-medium ms-auto"
          data-testid={`resource-delete-${r.resource_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

/** Modal: pick a chapter, then add a YouTube / File / Link resource scoped
 * to that chapter. Wraps the existing `/staff/chapters/:id/resources`
 * endpoint so every approved student on the platform can see it from the
 * Chapter → Resources tab. */
function AttachToChapterModal({ onClose, onSaved }) {
  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState("");
  const [type, setType] = useState("youtube");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [fileId, setFileId] = useState(null);
  const [filename, setFilename] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/chapters").then((r) => {
      setChapters(r.data);
      if (r.data.length > 0) setChapterId(r.data[0].id);
    }).catch(() => { /* empty chapter list is fine */ });
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "resource");
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(r.data.file_id);
      setFilename(r.data.filename);
      if (!title) setTitle(r.data.filename);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!chapterId) return setErr("Please pick a chapter");
    if (!title.trim()) return setErr("Please enter a title");
    if (type === "youtube" && !url.trim()) return setErr("Please paste a YouTube URL");
    if (type === "file" && !fileId) return setErr("Please upload a file");
    if (type === "link" && !url.trim()) return setErr("Please paste a URL");
    setErr("");
    setSaving(true);
    try {
      await api.post(`/staff/chapters/${chapterId}/resources`, {
        type,
        title: title.trim(),
        description: description.trim(),
        youtube_url: type === "youtube" ? url.trim() : null,
        external_url: type === "link" ? url.trim() : null,
        file_id: type === "file" ? fileId : null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const TYPE_TABS = [
    { k: "youtube", label: "YouTube", Icon: Youtube },
    { k: "file", label: "File", Icon: Upload },
    { k: "link", label: "Link", Icon: LinkIcon },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="attach-chapter-modal">
      <div className="bg-bone w-full max-w-lg rounded-3xl border border-border overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="font-display text-lg font-semibold">Attach resource to chapter</div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary" data-testid="attach-chapter-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="px-6 py-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Chapter</label>
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              data-testid="attach-chapter-select"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
            >
              {chapters.length === 0 && <option value="">Loading chapters…</option>}
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>
                  Chapter {String(c.number).padStart(2, "0")} · {c.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Kind</label>
            <div className="mt-1 inline-flex items-center gap-1 bg-white border border-border rounded-xl p-1">
              {TYPE_TABS.map(({ k, label, Icon }) => (
                <button
                  key={k}
                  onClick={() => setType(k)}
                  data-testid={`attach-chapter-type-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                    type === k ? "bg-sage text-white" : "text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="attach-chapter-title"
              placeholder={type === "youtube" ? "e.g. Intro lecture video" : "e.g. Lecture slides"}
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
            />
          </div>

          {type === "youtube" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">YouTube URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="attach-chapter-youtube-url"
                placeholder="https://youtube.com/watch?v=…"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
              />
            </div>
          )}

          {type === "link" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="attach-chapter-link-url"
                placeholder="https://…"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
              />
            </div>
          )}

          {type === "file" && (
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">File (≤5 MB)</label>
              <label className="mt-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border bg-white hover:border-sage cursor-pointer transition">
                <Upload className="w-5 h-5 text-sage" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{filename || (uploading ? "Uploading…" : "Click to upload PDF, slides, image…")}</div>
                  <div className="text-[11px] text-muted-foreground">PDF · DOCX · PPTX · JPG · PNG · CSV · TXT</div>
                </div>
                <input type="file" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="attach-chapter-file-input" />
              </label>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="attach-chapter-description"
              className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage min-h-[50px]"
            />
          </div>

          {err && <div className="text-xs text-terracotta" data-testid="attach-chapter-error">{err}</div>}
        </div>

        <footer className="px-6 py-4 border-t border-border bg-white flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-sage" data-testid="attach-chapter-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading || !chapterId}
            data-testid="attach-chapter-save"
            className="bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all"
          >
            {saving ? "Saving…" : "Attach"}
          </button>
        </footer>
      </div>
    </div>
  );
}
